// openEHR AM model — BMM package org.openehr.am.aom2.profile
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import * as openehr_base from "../../base/mod.ts";

/**
 * Profile of common settings relating to use of reference model(s) and terminology for a given archetype developing organisation.
 */
export class AOM_PROFILE {
  /**
   * Internal storage for profile_name
   * @protected
   */
  protected _profile_name?: openehr_base.String;

  /**
   * Name of this profile, usually based on the publisher it pertains to e.g. "openEHR", "CDISC", etc.
   */
  get profile_name(): string | undefined {
    return this._profile_name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for profile_name.
   * Use this to access openehr_base.String methods.
   */
  get $profile_name(): openehr_base.String | undefined {
    return this._profile_name;
  }

  /**
   * Sets profile_name from either a primitive value or openehr_base.String wrapper.
   */
  set profile_name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._profile_name = undefined;
    } else if (typeof val === "string") {
      this._profile_name = openehr_base.String.from(val);
    } else {
      this._profile_name = val;
    }
  }

  /**
   * Internal storage for archetype_parent_class
   * @protected
   */
  protected _archetype_parent_class?: openehr_base.String;

  /**
   * States a class from the Reference Model that provides archetyping capability in RM data structures. This attribute is optional, and there need be no such class in the RM. Defining it here has the effect in tools that the class tree under which archetypes are arranged contains only RM classes inheriting from this class.
   */
  get archetype_parent_class(): string | undefined {
    return this._archetype_parent_class?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for archetype_parent_class.
   * Use this to access openehr_base.String methods.
   */
  get $archetype_parent_class(): openehr_base.String | undefined {
    return this._archetype_parent_class;
  }

  /**
   * Sets archetype_parent_class from either a primitive value or openehr_base.String wrapper.
   */
  set archetype_parent_class(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._archetype_parent_class = undefined;
    } else if (typeof val === "string") {
      this._archetype_parent_class = openehr_base.String.from(val);
    } else {
      this._archetype_parent_class = val;
    }
  }

  /**
   * Internal storage for archetype_data_value_parent_class
   * @protected
   */
  protected _archetype_data_value_parent_class?: openehr_base.String;

  /**
   * This attribute defines a base class from the Reference Model whose descendants are considered to be 'logical data types', i.e. of some higher level than the built-in primitive types String, Integer etc. This attribute is optional, even if the RM does have such a class, and is only used to help tooling to provide more intelligent display.
   */
  get archetype_data_value_parent_class(): string | undefined {
    return this._archetype_data_value_parent_class?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for archetype_data_value_parent_class.
   * Use this to access openehr_base.String methods.
   */
  get $archetype_data_value_parent_class(): openehr_base.String | undefined {
    return this._archetype_data_value_parent_class;
  }

  /**
   * Sets archetype_data_value_parent_class from either a primitive value or openehr_base.String wrapper.
   */
  set archetype_data_value_parent_class(
    val: string | openehr_base.String | undefined,
  ) {
    if (val === undefined || val === null) {
      this._archetype_data_value_parent_class = undefined;
    } else if (typeof val === "string") {
      this._archetype_data_value_parent_class = openehr_base.String.from(val);
    } else {
      this._archetype_data_value_parent_class = val;
    }
  }

  /**
   * Mappings from AOM built-in types to actual types in RM: whenever the type name is encountered in an archetype, it is mapped to a specific RM type.
   */
  aom_rm_type_mappings?: undefined;
  /**
   * Internal storage for archetype_visualise_descendants_of
   * @protected
   */
  protected _archetype_visualise_descendants_of?: openehr_base.String;

  /**
   * If \`_archetype_parent_class_\` is not set, designate a class whose descendants should be made visible in tree and grid renderings of the archetype definition.
   */
  get archetype_visualise_descendants_of(): string | undefined {
    return this._archetype_visualise_descendants_of?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for archetype_visualise_descendants_of.
   * Use this to access openehr_base.String methods.
   */
  get $archetype_visualise_descendants_of(): openehr_base.String | undefined {
    return this._archetype_visualise_descendants_of;
  }

  /**
   * Sets archetype_visualise_descendants_of from either a primitive value or openehr_base.String wrapper.
   */
  set archetype_visualise_descendants_of(
    val: string | openehr_base.String | undefined,
  ) {
    if (val === undefined || val === null) {
      this._archetype_visualise_descendants_of = undefined;
    } else if (typeof val === "string") {
      this._archetype_visualise_descendants_of = openehr_base.String.from(val);
    } else {
      this._archetype_visualise_descendants_of = val;
    }
  }

  /**
   * Allowed type substitutions: Actual RM type names keyed by AOM built-in types which can substitute for them in an archetype. E.g. \`<key = "ISO8601_DATE", value = "String">\` means that if RM property \`TYPE._some_property_\` is of type \`String\`, an \`ISO8601_DATE\` is allowed at that position in the archetype.
   */
  aom_rm_type_substitutions?: undefined;
  /**
   * List of mappings of lifecycle state names used in archetypes to AOM lifecycle state names:
   *
   * * key = source lifecycle state;
   * * value = AOM lifecycle state.
   */
  aom_lifecycle_mappings?: undefined;
  /**
   * Equivalences of RM primitive types to in-built set of primitive types. Used to determine which AOM \`C_PRIMITIVE_OBJECT\` descendant is used for a primitive type. Typical entries:
   *
   * * \`value\`	\`key\`
   * * "Real"	"Double"
   * * "Integer"	"Integer64"
   */
  rm_primitive_type_equivalences?: undefined;
}

/**
 * Data object expressing a mapping between two types.
 */
export class AOM_TYPE_MAPPING {
  /**
   * Internal storage for source_class_name
   * @protected
   */
  protected _source_class_name?: openehr_base.String;

  /**
   * Name of the AOM type being mapped to an RM type.
   */
  get source_class_name(): string | undefined {
    return this._source_class_name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for source_class_name.
   * Use this to access openehr_base.String methods.
   */
  get $source_class_name(): openehr_base.String | undefined {
    return this._source_class_name;
  }

  /**
   * Sets source_class_name from either a primitive value or openehr_base.String wrapper.
   */
  set source_class_name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._source_class_name = undefined;
    } else if (typeof val === "string") {
      this._source_class_name = openehr_base.String.from(val);
    } else {
      this._source_class_name = val;
    }
  }

  /**
   * Internal storage for target_class_name
   * @protected
   */
  protected _target_class_name?: openehr_base.String;

  /**
   * Name of the RM type in the mapping.
   */
  get target_class_name(): string | undefined {
    return this._target_class_name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for target_class_name.
   * Use this to access openehr_base.String methods.
   */
  get $target_class_name(): openehr_base.String | undefined {
    return this._target_class_name;
  }

  /**
   * Sets target_class_name from either a primitive value or openehr_base.String wrapper.
   */
  set target_class_name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._target_class_name = undefined;
    } else if (typeof val === "string") {
      this._target_class_name = openehr_base.String.from(val);
    } else {
      this._target_class_name = val;
    }
  }

  /**
   * List of mappings of properties of this type to another type.
   */
  property_mappings?: undefined;
}

/**
 * Data object expressing a mapping between two class properties.
 */
export class AOM_PROPERTY_MAPPING {
  /**
   * Internal storage for source_property_name
   * @protected
   */
  protected _source_property_name?: openehr_base.String;

  /**
   * Name of property in source class.
   */
  get source_property_name(): string | undefined {
    return this._source_property_name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for source_property_name.
   * Use this to access openehr_base.String methods.
   */
  get $source_property_name(): openehr_base.String | undefined {
    return this._source_property_name;
  }

  /**
   * Sets source_property_name from either a primitive value or openehr_base.String wrapper.
   */
  set source_property_name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._source_property_name = undefined;
    } else if (typeof val === "string") {
      this._source_property_name = openehr_base.String.from(val);
    } else {
      this._source_property_name = val;
    }
  }

  /**
   * Internal storage for target_property_name
   * @protected
   */
  protected _target_property_name?: openehr_base.String;

  /**
   * Name of property in target class.
   */
  get target_property_name(): string | undefined {
    return this._target_property_name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for target_property_name.
   * Use this to access openehr_base.String methods.
   */
  get $target_property_name(): openehr_base.String | undefined {
    return this._target_property_name;
  }

  /**
   * Sets target_property_name from either a primitive value or openehr_base.String wrapper.
   */
  set target_property_name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._target_property_name = undefined;
    } else if (typeof val === "string") {
      this._target_property_name = openehr_base.String.from(val);
    } else {
      this._target_property_name = val;
    }
  }
}
